import type { ActionEventName } from "./types";

export interface MobileAsset {
  path: string;
  loop: boolean;
  priority: number;
  cooldown_seconds: number;
  interruptible: boolean;
  fallback: string;
}

export interface MobileManifest {
  schema_version: number;
  event_mapping: Record<ActionEventName, string>;
  assets: Record<string, MobileAsset>;
}

export class RobotPlayer {
  private manifest: MobileManifest | null = null;
  private current = "";
  private queue: string[] = [];
  private active = true;

  constructor(private readonly video: HTMLVideoElement) {
    this.video.addEventListener("ended", () => this.playNext());
    this.video.addEventListener("timeupdate", () => this.accelerateReturn());
    this.video.addEventListener("error", () => {
      this.queue = [];
      if (this.current !== "idle") void this.playAsset("idle", true);
    });
  }

  async initialize(): Promise<void> {
    const response = await fetch("/assets/manifest.json", { cache: "force-cache" });
    if (!response.ok) throw new Error("机器人素材清单加载失败");
    this.manifest = await response.json() as MobileManifest;
    await this.playAsset("idle", true);
  }

  async unlock(): Promise<void> {
    this.active = true;
    if (!this.video.src) await this.initialize();
    await this.video.play();
    void this.preloadActions();
  }

  setActive(active: boolean): void {
    this.active = active;
    if (!active) this.video.pause();
  }

  trigger(eventName: ActionEventName): void {
    if (!this.active || !this.manifest) return;
    const nextName = this.manifest.event_mapping[eventName];
    if (!this.manifest.assets[nextName]) return;

    if (eventName === "USER_ENTERED") {
      this.enqueue([nextName, "invite"]);
      return;
    }

    // A real interaction always takes precedence over a pending attraction clip.
    this.queue = this.queue.filter((name) => name !== "notice" && name !== "invite");
    this.enqueue([nextName]);
  }

  private enqueue(names: string[]): void {
    const valid = names.filter((name) => this.manifest?.assets[name]);
    if (this.current === "idle" || !this.current) {
      const [first, ...rest] = valid;
      this.queue = rest;
      if (first) void this.playAsset(first, false);
      return;
    }
    for (const name of valid) {
      if (name !== this.current && this.queue.at(-1) !== name) this.queue.push(name);
    }
    this.queue = this.queue.slice(-2);
  }

  private async playAsset(name: string, force: boolean): Promise<void> {
    const asset = this.manifest?.assets[name];
    if (!asset) return;
    const shouldChangeSource = this.current !== name || !this.video.src;
    if (shouldChangeSource) {
      this.current = name;
      this.video.src = `/assets/videos/${asset.path}`;
      this.video.loop = asset.loop;
      this.video.playbackRate = name === "idle" ? 1 : 1.12;
      this.video.load();
    } else if (!asset.loop || force) {
      this.video.currentTime = 0;
    }
    try {
      await this.video.play();
    } catch {
      // The start button calls unlock(), satisfying iOS's user-gesture requirement.
    }
  }

  private playNext(): void {
    const next = this.queue.shift();
    if (next) {
      void this.playAsset(next, false);
      return;
    }
    void this.playAsset("idle", true);
  }

  private accelerateReturn(): void {
    if (this.current === "idle" || !Number.isFinite(this.video.duration) || this.video.duration <= 0) return;
    if (this.video.currentTime / this.video.duration >= 0.72 && this.video.playbackRate < 1.5) {
      this.video.playbackRate = 1.55;
    }
  }

  private async preloadActions(): Promise<void> {
    if (!this.manifest) return;
    const paths = Object.values(this.manifest.assets)
      .filter((asset) => asset.path !== this.manifest!.assets.idle.path)
      .map((asset) => `/assets/videos/${asset.path}`);
    await Promise.allSettled(paths.map((path) => fetch(path, { cache: "force-cache" })));
  }
}
